/** Analytics: San Francisco's traffic, emissions, air quality and health metrics on the
 *  map by link, hexagon, city hexagon or census tract, at one hour of a weekday, with
 *  charts beside it: the metric through the day, the highest features, the selected
 *  one, and for tracts the metric by neighbourhood income. Every scenario the manifest
 *  lists as built can be shown (V/C by scenario is the 'vc' metric, drawn by the
 *  pricing map). */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { getViewRoads, roadTier } from './api';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, Cell,
} from 'recharts';
import AnalyticsMap from './AnalyticsMap';
import { visibleScenarios } from '../../config/features';
import {
  LEVELS, METRICS, metricOf, getLayer, getManifest, scaleFor, fmtValue,
  hourlyProfile, featureProfile, topFeatures, byIncomeQuintile,
} from './analytics';

// Chart ink: a neutral blue for data, so red and green only ever mean worse and better
// on the map; recessive chrome from the reference palette.
const SERIES = '#3987e5';
const GRID = '#2c2c2a';
const MUTED = '#898781';
const SECONDARY = '#c3c2b7';
const AXIS = { fill: MUTED, fontSize: 11 };
const NO_AXIS_LINE = { axisLine: false, tickLine: false };
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00–${String((h + 1) % 24).padStart(2, '0')}:00`;

/** Metric choice, grouped by source; every Analytics map shows it. `illustrativeHealth`:
 *  the scenario's BenMAP figures are inferred, not San Francisco's own run, and say so. */
export function MetricPicker({ value, onChange, illustrativeHealth = false }) {
  const groups = [...new Set(METRICS.map((m) => m.group))];
  return (
    <label className="ws-flow-field">
      <span className="ws-muted">Metric</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {groups.map((g) => (
          <optgroup key={g} label={g}>
            {METRICS.filter((m) => m.group === g).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}{m.unit ? ` (${m.unit})` : ''}{m.source === 'BenMAP' && illustrativeHealth ? ' · illustrative' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

/** The links in view from the stored runs, as the V/C map draws them: every road of the
 *  classes the zoom shows (roadTier), for one run at one hour. Waits for the view and the
 *  hour to settle; the previous view stays up while the next loads. `run` null: none. */
function useViewLinks(workshop, run, hour, view) {
  const base = run ? `${run}:${hour}` : null;
  const key = base && view ? `${base}:${view.bounds.map((x) => x.toFixed(4)).join(',')}:${roadTier(view.zoom).id}` : null;
  const [state, setState] = useState({ key: null, base: null, loading: false, data: null, error: null });
  const viewNow = useRef(view);
  viewNow.current = view;
  const lastBase = useRef(base);
  useEffect(() => {
    if (!key || !workshop) return undefined;
    // A pan or zoom is already settled by the map; only a new hour or run (a slider drag)
    // waits a moment. A request overtaken by the next one is aborted, not left to run.
    const wait = lastBase.current === base ? 0 : 150;
    lastBase.current = base;
    const ctl = new AbortController();
    let cancelled = false;
    // The previous roads stay drawn, with the spinner, until the new ones arrive.
    setState((prev) => ({ key, base, loading: true, data: prev.data, error: null }));
    const t = setTimeout(() => {
      Promise.resolve()
        .then(() => getViewRoads(workshop, run, hour, viewNow.current, ctl.signal))
        .then((data) => { if (!cancelled) setState({ key, base, loading: false, data, error: null }); })
        .catch((e) => { if (!cancelled && e.name !== 'AbortError') setState({ key, base, loading: false, data: null, error: e.message }); });
    }, wait);
    return () => { cancelled = true; clearTimeout(t); ctl.abort(); };
  }, [key, base, workshop, run, hour]);
  if (!key) return { loading: !!base, data: null, error: null };
  if (state.key === key) return state;
  return { loading: true, data: state.data, error: null };
}

export default function AnalyticsPage({
  cbsa, metricId, onMetric, scenarioId, onScenario, level, onLevel, hour, onHour, header,
  workshop = null, chargedHours = [],
}) {
  const metric = metricOf(metricId);
  // A metric that does not exist at the chosen level moves to its first level.
  const lvl = metric.levels.includes(level) ? level : metric.levels[0];
  useEffect(() => { if (lvl !== level) onLevel(lvl); }, [lvl, level, onLevel]);
  const hourly = metric.hourly !== false;

  const [state, setState] = useState({ key: null, layer: null, error: null });
  const [selected, setSelected] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [fill, setFill] = useState(0.4);
  useEffect(() => { getManifest(cbsa).then(setManifest).catch((e) => setState({ key: null, layer: null, error: e.message })); }, [cbsa]);
  // Only a built scenario can be shown; one picked elsewhere that is not built falls back.
  const built = manifest ? manifest.scenarios.filter((s) => s.available) : [];
  const scenario = built.find((s) => s.id === scenarioId) || built.find((s) => s.id === 'baseline') || built[0] || null;
  const scen = scenario ? scenario.id : null;
  const key = scen ? `${scen}:${lvl}:${metricId}` : null;
  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setSelected(null);
    getLayer(cbsa, scen, lvl, metricId)
      .then((layer) => { if (!cancelled) setState({ key, layer, error: null }); })
      .catch((e) => { if (!cancelled) setState({ key, layer: null, error: e.message }); });
    return () => { cancelled = true; };
  }, [cbsa, scen, lvl, metricId, key]);

  const layer = state.key === key ? state.layer : null;
  const scale = useMemo(() => (layer ? scaleFor(layer, metric) : null), [layer, metric]);
  const h = hourly ? hour : 0;
  const illustrativeHealth = !scenario || scenario.health_source !== 'benmap';
  const illustrative = metric.source === 'BenMAP' && illustrativeHealth;
  // Links on the map come from the database for the roads in view, like the V/C map; a
  // pricing scenario's run exists for its charged hours only, the other hours are the
  // baseline's. The charts beside the map keep the static file.
  const [mapView, setMapView] = useState(null);
  const linkRun = !workshop || lvl !== 'link' || !scen ? null
    : scen !== 'baseline' && chargedHours.includes(h) ? scen : 'baseline';
  const viewLinks = useViewLinks(workshop, linkRun, h, mapView);

  return (
    <>
      {header}
      <div className="ws-layout ws-layout-region">
        <AnalyticsMap
          level={lvl}
          layer={layer || { features: [], values: () => null }}
          metric={metric}
          hour={h}
          colorOf={scale ? scale.colorOf : () => [0, 0, 0]}
          selected={selected}
          onSelect={setSelected}
          viewLinks={lvl === 'link' ? viewLinks.data : null}
          onViewChange={setMapView}
          fill={fill}
          legend={<AnalyticsLegend metric={metric} rows={scale ? scale.rows : []} illustrative={illustrative} level={lvl} />}
        >
          <div className="ws-map-top">
            <div className="ws-map-panel ws-an-panel">
              <div className="ws-map-panel-title">Analytics</div>
              <label className="ws-flow-field">
                <span className="ws-muted">Scenario</span>
                <select value={scen || ''} onChange={(e) => onScenario(e.target.value)} disabled={!manifest}>
                  {(manifest ? visibleScenarios(manifest.scenarios) : []).map((s) => (
                    <option key={s.id} value={s.id} disabled={!s.available}>
                      {s.code} · {s.name}{s.available ? '' : ' (not modelled yet)'}
                    </option>
                  ))}
                </select>
              </label>
              <MetricPicker value={metricId} onChange={onMetric} illustrativeHealth={illustrativeHealth} />
              <div className="ws-flow-field">
                <span className="ws-muted">Level</span>
                <div className="ws-toggle" role="tablist">
                  {LEVELS.map((l) => {
                    const ok = metric.levels.includes(l.id);
                    return (
                      <button
                        key={l.id}
                        role="tab"
                        aria-selected={lvl === l.id}
                        className={lvl === l.id ? 'on' : ''}
                        disabled={!ok}
                        title={ok ? undefined : `${metric.label} is not available by ${l.label.toLowerCase()}`}
                        onClick={() => onLevel(l.id)}
                      >
                        {l.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {lvl !== 'link' && (
                <label className="ws-flow-field">
                  <span className="ws-muted">Fill · {Math.round(fill * 100)}%</span>
                  <input type="range" min="0.1" max="0.9" step="0.05" value={fill}
                    onChange={(e) => setFill(Number(e.target.value))} />
                </label>
              )}
              <label className="ws-flow-field">
                <span className="ws-muted">Hour {hourly ? `· ${hourLabel(hour)}` : '· annual figure'}</span>
                <input type="range" min="0" max="23" step="1" value={hour} disabled={!hourly}
                  onChange={(e) => onHour(Number(e.target.value))} />
              </label>
            </div>
          </div>
          <div className="ws-map-status">
            {state.error && (state.key === key || !manifest) ? <span className="ws-error-text">Could not load: {state.error}</span>
              : !layer ? <><FaSpinner className="ws-spin" /> Loading…</>
                : lvl === 'link' && linkRun ? (
                  viewLinks.data ? (
                    <>
                      {`${viewLinks.data.features.length.toLocaleString()} links in view`}
                      {mapView && roadTier(mapView.zoom).what ? ` (${roadTier(mapView.zoom).what}; zoom in for every road)` : ''}
                      {` · ${scenario.name}`}
                      {viewLinks.loading && <> · <FaSpinner className="ws-spin" /></>}
                    </>
                  ) : viewLinks.error ? (
                    `${layer.features.length.toLocaleString()} links (San Francisco and major roads; the roads in view could not be loaded) · ${scenario.name}`
                  ) : <><FaSpinner className="ws-spin" /> Loading roads in view…</>
                )
                  : `${layer.features.length.toLocaleString()} ${LEVELS.find((l) => l.id === lvl).label.toLowerCase()}s${illustrative ? ' · illustrative' : ''} · ${scenario.name}`}
          </div>
        </AnalyticsMap>

        <div className="ws-col">
          {layer && selected != null && layer.features[selected] && (
            <SelectedPanel layer={layer} index={selected} metric={metric} hour={h} level={lvl} onClear={() => setSelected(null)} />
          )}
          {layer && hourly && <ProfilePanel layer={layer} metric={metric} hour={h} selected={selected} level={lvl} />}
          {layer && !hourly && <TotalPanel layer={layer} metric={metric} />}
          {layer && <TopPanel layer={layer} metric={metric} hour={h} hourly={hourly} onPick={setSelected} level={lvl} />}
          {layer && lvl === 'tract' && <EquityPanel layer={layer} metric={metric} hour={h} illustrative={illustrative} />}
        </div>
      </div>
    </>
  );
}

function AnalyticsLegend({ metric, rows, illustrative, level }) {
  return (
    <div className="ws-legend">
      <div className="ws-legend-title">{metric.label}</div>
      <div className="ws-legend-sub">{metric.unit}{level === 'link' ? ', per link' : ''}{metric.hourly === false ? '' : ', in the hour'}</div>
      {rows.map((r) => (
        <div className="ws-legend-row" key={r.label}>
          <span className={level === 'link' ? 'ws-swatch' : 'ws-swatch-area'} style={{ background: `rgb(${r.color.join(',')})` }} />{r.label}
        </div>
      ))}
      <div className="ws-legend-row"><span className={level === 'link' ? 'ws-swatch' : 'ws-swatch-area'} style={{ background: 'rgba(140,140,140,0.35)' }} />No data</div>
      {illustrative && (
        <div className="ws-legend-note">Illustrative estimate.</div>
      )}
    </div>
  );
}

function Panel({ title, sub, children, aside }) {
  return (
    <div className="ws-panel">
      <div className="ws-panel-title ws-an-title">{title}{aside}</div>
      {sub && <div className="ws-panel-sub">{sub}</div>}
      {children}
    </div>
  );
}

function ChartTip({ active, payload, metric }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="ws-an-tip">
      <div>{p.label ?? p.name}</div>
      <b>{fmtValue(p.value, metric)} {metric.unit}</b>
    </div>
  );
}

/** What the charts beside the map add up, in the reader's words. */
const scopeOf = (level) => ({
  link: "San Francisco's streets and the region's major roads",
  hex: 'the Bay Area',
  tract: "the Bay Area's census tracts",
}[level] || 'the Bay Area');

function ProfilePanel({ layer, metric, hour, selected, level }) {
  const one = selected != null && layer.features[selected];
  const data = useMemo(() => (one ? featureProfile(layer, selected) : hourlyProfile(layer, metric)), [layer, metric, selected, one]);
  return (
    <Panel
      title="Through the day"
      sub={one ? layer.features[selected].name : `${metric.sum ? 'Total' : 'Average'} over ${scopeOf(level)}, each hour`}
    >
      <div className="ws-an-chart">
        <ResponsiveContainer width="100%" height={170}>
          <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
            <defs>
              <linearGradient id="ws-an-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={SERIES} stopOpacity={0.32} />
                <stop offset="100%" stopColor={SERIES} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis dataKey="hour" tick={AXIS} {...NO_AXIS_LINE} tickFormatter={(v) => (v % 6 === 0 ? `${String(v).padStart(2, '0')}:00` : '')} interval={0} />
            <YAxis tick={AXIS} {...NO_AXIS_LINE} width={52} domain={[0, 'auto']} tickFormatter={(v) => fmtValue(v, metric)} />
            <Tooltip content={<ChartTip metric={metric} />} cursor={{ stroke: SECONDARY, strokeWidth: 1 }} />
            <ReferenceLine x={hour} stroke={SECONDARY} strokeOpacity={0.6} strokeDasharray="3 3" />
            <Area
              type="monotone"
              dataKey="value"
              stroke={SERIES}
              strokeWidth={2}
              fill="url(#ws-an-area)"
              dot={false}
              activeDot={{ r: 4, fill: SERIES, stroke: '#242424', strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

function TotalPanel({ layer, metric }) {
  const t = hourlyProfile(layer, metric)[0].value;
  const pop = layer.features.reduce((s, f) => s + (f.props?.pop_total || 0), 0);
  return (
    <Panel title={metric.sum ? 'Bay Area total' : 'Bay Area average'} sub={metric.label}>
      <div className="ws-an-big">{fmtValue(t, metric)} <span className="ws-muted">{metric.unit}</span></div>
      {metric.sum && pop > 0 && (
        <div className="ws-muted">{fmtValue((1e5 * t) / pop, { digits: 2 })} per 100k residents ({pop.toLocaleString()} residents)</div>
      )}
    </Panel>
  );
}

function TopPanel({ layer, metric, hour, hourly, onPick, level }) {
  // Links and hexagons are named after roads: one entry per name, its highest feature.
  const byName = level === 'link' || level === 'hex';
  const top = useMemo(() => topFeatures(layer, hour, 8, { byName }), [layer, hour, byName]);
  const when = hourly ? hourLabel(hour) : 'Per year';
  const each = { link: "each road's highest link", hex: "each place's highest hexagon" }[level];
  return (
    <Panel title="Highest" sub={each ? `${when} · ${each}` : when}>
      <div className="ws-an-chart">
        <ResponsiveContainer width="100%" height={Math.max(80, top.length * 26)}>
          <BarChart data={top} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={130} tick={AXIS} {...NO_AXIS_LINE} tickFormatter={(v) => (v.length > 20 ? `${v.slice(0, 19)}…` : v)} />
            <Tooltip content={<ChartTip metric={metric} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar dataKey="value" fill={SERIES} radius={[0, 4, 4, 0]} barSize={14} isAnimationActive={false} onClick={(d) => { const i = d?.payload?.i ?? d?.i; if (i != null) onPick(i); }} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

// Ordered income groups, not good or bad: five steps of the chart blue, lowest income
// darkest (validated as an ordinal ramp on the panel #242424, darkest step 2.3:1).
const QUINTILE_COLORS = ['#1c5cab', '#2a78d6', '#5598e7', '#86b6ef', '#b7d3f6'];

function EquityPanel({ layer, metric, hour, illustrative }) {
  const data = useMemo(() => byIncomeQuintile(layer, metric, hour), [layer, metric, hour]);
  return (
    <Panel
      title="By neighbourhood income"
      sub={metric.sum ? `${metric.label} per 100k residents, tracts grouped by median household income` : `${metric.label}, resident-weighted, tracts grouped by median household income`}
      aside={null}
    >
      <div className="ws-an-chart">
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis dataKey="label" tick={AXIS} {...NO_AXIS_LINE} interval={0} />
            <YAxis tick={AXIS} {...NO_AXIS_LINE} width={52} domain={[0, 'auto']} tickFormatter={(v) => fmtValue(v, { digits: 2 })} />
            <Tooltip content={<ChartTip metric={{ ...metric, unit: metric.sum ? 'per 100k' : metric.unit }} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false}>
              {data.map((d, i) => <Cell key={d.label} fill={QUINTILE_COLORS[i]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="ws-muted">Census tracts grouped into fifths by median household income.</div>
    </Panel>
  );
}

function SelectedPanel({ layer, index, metric, hour, level, onClear }) {
  const f = layer.features[index];
  const p = f.props || {};
  return (
    <Panel title={f.name} aside={<button className="ws-hex-clear ws-an-clear" onClick={onClear}>Clear</button>}>
      <div className="ws-hex-facts">
        <div><span className="ws-muted">{metric.label}</span>{fmtValue(layer.values(index, hour), metric)} {metric.unit}</div>
        {level === 'link' && <div><span className="ws-muted">Road type</span>{f.kind || '—'}</div>}
        {level === 'tract' && (
          <>
            <div><span className="ws-muted">Residents</span>{(p.pop_total || 0).toLocaleString()}</div>
            <div><span className="ws-muted">Median income</span>{p.median_income ? `$${p.median_income.toLocaleString()}` : '—'}</div>
            <div><span className="ws-muted">Low-income households</span>{p.low_income_share == null ? '—' : `${Math.round(p.low_income_share * 100)}%`}</div>
            <div>
              <span className="ws-muted">Road PM2.5, day mean</span>
              {layer.annualOf && layer.annualOf(index, 'deltaq_annual_ugm3') != null ? `${layer.annualOf(index, 'deltaq_annual_ugm3')} µg/m³` : '—'}
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

